import { Box, Button, Chip, Menu, MenuItem, TextField, Typography } from "@mui/material"
import { Link as MuiLink } from "@mui/material"
import { CheckCircle, ChevronDown, Eye, FileText, RotateCcw } from "lucide-react"
import { useCallback, useState, type JSX } from "react"
import { Link as RouterLink } from "react-router-dom"

import { useCan } from "@/auth/permissions"
import { ConfirmDialog } from "@/components/ui/confirm-dialog"
import { useToast } from "@/components/ui/use-toast"
import { Money } from "@crm-src/components/money"
import { ResellerNetworkColumn } from "@crm-src/components/reseller-network-column"
import { useResellerPagedList } from "@crm-src/hooks/useResellerPagedList"
import { invoicesApi, type InvoiceListFilters } from "@crm-src/lib/invoices-api"

interface DocumentRow {
  id: string
  invoice_number?: string
  document_type?: string
  issue_date?: string
  due_date?: string
  total_amount?: number | string
  payment_status?: string
  status?: string
  customer_company_name?: string
  customer_name?: string
  customer_id?: string
  /** Derived on the server: `expired` by date on a reseller-requested quote. */
  effective_status?: string
  /** A quote the reseller requested and decided on XOP Anytime (read-only in the CRM). */
  locked?: boolean
  reseller_quote?: { requested_by_email?: string } | null
}

interface ResellerDocumentsListProps {
  title: string
  entityNoun: string
  emptyMessage: string
  /** The server-side filter that defines this section. */
  filters: InvoiceListFilters
  "data-cy"?: string | undefined
}

/** Today as `YYYY-MM-DD`, in the operator's own timezone — the default paid date. */
function todayIso(): string {
  const d = new Date()
  const pad = (n: number) => String(n).padStart(2, "0")
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`
}

function formatDate(value?: string): string {
  if (!value) return "—"
  return new Date(value).toLocaleDateString("en-US", { year: "numeric", month: "short", day: "numeric" })
}

/**
 * ONE list of invoices or quotes, filtered server-side.
 *
 * Both money tabs render one of these — which documents a tab shows is a
 * `filters` prop (`bill_to_reseller_id`: XOP's documents TO the reseller)
 * rather than a second component. A quote list and an invoice list differ only
 * in `document_type` and their status field, so a second copy would be a copy.
 *
 * Every row has a VIEW button into the ordinary quote / invoice page
 * (2026-09-15). An INVOICE row also has a Status menu — Mark as paid (with the
 * date it was paid) or Mark as unpaid — for a holder of `crm:edit`, through the
 * same crm-api `mark_as_paid` / `mark_as_unpaid` endpoints the Invoices page
 * uses, so both surfaces write one way. Quotes get no status menu: a quote's
 * state is the reseller's decision on XOP Anytime, not ours to set here.
 *
 * Every figure goes through `<Money>`, the CRM's one redaction choke point.
 * That is belt-and-braces here (a viewer without `crm:financials` never sees
 * the tab that mounts this), but `financial-wall.test.ts` fails CI on a raw
 * money render on a finance-reachable page and it is right to.
 */
export function ResellerDocumentsList({
  title,
  entityNoun,
  emptyMessage,
  filters,
  "data-cy": dataCy,
}: ResellerDocumentsListProps): JSX.Element {
  const { toast } = useToast()
  const canChangeStatus = useCan("crm:edit")
  const [menu, setMenu] = useState<{ anchor: HTMLElement; row: DocumentRow } | null>(null)
  const [pendingPaid, setPendingPaid] = useState<DocumentRow | null>(null)
  const [paidDate, setPaidDate] = useState(todayIso)
  const [pendingUnpaid, setPendingUnpaid] = useState<DocumentRow | null>(null)

  // Serialised so the fetcher's identity changes when a filter does, without
  // depending on the object literal's reference (a new object every render).
  const filterKey = JSON.stringify(filters)

  const list = useResellerPagedList<DocumentRow>(
    useCallback(
      async ({ page, perPage, search }) => {
        const result = await invoicesApi.list({
          ...(JSON.parse(filterKey) as InvoiceListFilters),
          page,
          per_page: perPage,
          include_customer: true,
          ...(search ? { search } : {}),
        })
        const meta = result.meta?.pagination
        return {
          rows: (result.data ?? []) as DocumentRow[],
          totalCount: meta?.total_count ?? 0,
          totalPages: meta?.total_pages ?? 1,
        }
      },
      [filterKey],
    ),
  )

  /** Run one status change, report it, and re-read the page it happened on.
   * Throws on failure so the ConfirmDialog stays open for another try. */
  const changeStatus = async (
    row: DocumentRow,
    call: () => Promise<{ success: boolean; errors?: string[] }>,
    done: string,
  ): Promise<void> => {
    const result = await call()
    const label = row.invoice_number || `#${row.id}`
    if (!result.success) {
      toast({
        variant: "destructive",
        title: `Couldn't update ${label}`,
        description: result.errors?.[0] ?? "The CRM refused the change.",
      })
      throw new Error("status change failed")
    }
    toast({ title: `${label} ${done}` })
    list.refresh()
  }

  return (
    <>
    <ResellerNetworkColumn<DocumentRow>
      data-cy={dataCy}
      title={title}
      icon={<FileText size={18} />}
      entityNoun={entityNoun}
      emptyMessage={emptyMessage}
      list={list}
      renderRow={(row) => {
        const isQuote = row.document_type === "quote"
        const state = isQuote ? row.effective_status || row.status : row.payment_status
        const detailPath = isQuote ? `/crm/quotes/${row.id}` : `/crm/invoices/${row.id}`

        return (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              gap: 1.5,
              p: 1.5,
              bgcolor: "surfaceSoft",
              borderRadius: 1,
            }}
          >
            <Box sx={{ minWidth: 0 }}>
              <Typography sx={{ fontSize: 14, fontWeight: 500 }}>
                <MuiLink component={RouterLink} to={detailPath}>
                  {row.invoice_number || `#${row.id}`}
                </MuiLink>
              </Typography>
              <Typography sx={{ fontSize: 12, color: "text.secondary", mt: 0.25 }}>
                {row.locked
                  ? `Requested on XOP Anytime${row.reseller_quote?.requested_by_email ? ` by ${row.reseller_quote.requested_by_email}` : ""}`
                  : row.customer_company_name || row.customer_name || "Addressed to the reseller"}
              </Typography>
              <Typography sx={{ fontSize: 12, color: "text.disabled", mt: 0.25 }}>
                Issued {formatDate(row.issue_date)}
                {row.due_date ? ` · due ${formatDate(row.due_date)}` : ""}
              </Typography>
            </Box>

            <Box sx={{ display: "flex", alignItems: "center", gap: 2, flexShrink: 0 }}>
              <Box sx={{ display: "flex", flexDirection: "column", alignItems: "flex-end", gap: 0.5 }}>
                <Typography sx={{ fontSize: 14, fontWeight: 600 }}>
                  <Money value={row.total_amount} />
                </Typography>
                {state && <Chip size="small" label={state} variant="outlined" />}
              </Box>
              <Box sx={{ display: "flex", flexDirection: "column", alignItems: "stretch", gap: 0.75 }}>
                <Button
                  component={RouterLink}
                  to={detailPath}
                  size="small"
                  variant="outlined"
                  startIcon={<Eye size={14} />}
                  data-cy="reseller-document-view"
                  aria-label={`View ${isQuote ? "quote" : "invoice"} ${row.invoice_number || row.id}`}
                >
                  View
                </Button>
                {!isQuote && canChangeStatus && (
                  <Button
                    size="small"
                    variant="text"
                    color="inherit"
                    endIcon={<ChevronDown size={14} />}
                    data-cy="reseller-document-status"
                    aria-haspopup="menu"
                    onClick={(e) => setMenu({ anchor: e.currentTarget, row })}
                  >
                    Status
                  </Button>
                )}
              </Box>
            </Box>
          </Box>
        )
      }}
    />

    <Menu
      anchorEl={menu?.anchor ?? null}
      open={menu !== null}
      onClose={() => setMenu(null)}
      anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      transformOrigin={{ vertical: "top", horizontal: "right" }}
    >
      {menu && menu.row.payment_status !== "paid" && (
        <MenuItem
          data-cy="reseller-document-mark-paid"
          onClick={() => {
            setPaidDate(todayIso())
            setPendingPaid(menu.row)
            setMenu(null)
          }}
        >
          <Box sx={{ mr: 1, display: "flex", color: "success.main" }}><CheckCircle size={16} /></Box>
          Mark as paid
        </MenuItem>
      )}
      {menu && menu.row.payment_status !== "unpaid" && (
        <MenuItem
          data-cy="reseller-document-mark-unpaid"
          onClick={() => {
            setPendingUnpaid(menu.row)
            setMenu(null)
          }}
        >
          <Box sx={{ mr: 1, display: "flex" }}><RotateCcw size={16} /></Box>
          Mark as unpaid
        </MenuItem>
      )}
    </Menu>

    <ConfirmDialog
      isOpen={pendingPaid !== null}
      onClose={() => setPendingPaid(null)}
      title="Mark invoice as paid"
      variant="default"
      confirmLabel="Mark as paid"
      description={
        pendingPaid ? `Record ${pendingPaid.invoice_number || `#${pendingPaid.id}`} as paid in full.` : ""
      }
      onConfirm={async () => {
        if (!pendingPaid) return
        const row = pendingPaid
        await changeStatus(row, () => invoicesApi.markAsPaid(row.id, paidDate), "marked as paid")
      }}
    >
      <TextField
        type="date"
        label="Paid on"
        size="small"
        fullWidth
        value={paidDate}
        onChange={(e) => setPaidDate(e.target.value)}
        InputLabelProps={{ shrink: true }}
        inputProps={{ max: todayIso(), "data-cy": "reseller-document-paid-date" }}
        sx={{ mt: 2 }}
      />
    </ConfirmDialog>

    <ConfirmDialog
      isOpen={pendingUnpaid !== null}
      onClose={() => setPendingUnpaid(null)}
      title="Mark invoice as unpaid"
      confirmLabel="Mark as unpaid"
      description={
        pendingUnpaid
          ? `Reopen ${pendingUnpaid.invoice_number || `#${pendingUnpaid.id}`}? Its paid date is cleared and it counts as outstanding again.`
          : ""
      }
      onConfirm={async () => {
        if (!pendingUnpaid) return
        const row = pendingUnpaid
        await changeStatus(row, () => invoicesApi.markAsUnpaid(row.id), "marked as unpaid")
      }}
    />
    </>
  )
}
